import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const ADMIN_UID = 'o2VVqpqGLvcPeXiNYe8FdGhXnBJ3';

export default function MessageGeneratorPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedType, setCopiedType] = useState(null);

  if (!currentUser || currentUser.uid !== ADMIN_UID) {
    navigate('/');
    return null;
  }

  useEffect(() => {
    loadCircles();
  }, []);

  const loadCircles = async () => {
    setLoading(true);
    const q = query(
      collection(db, 'artPingPongCircles'),
      where('createdBy', '==', currentUser.uid)
    );
    const snapshot = await getDocs(q);
    const circleData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setCircles(circleData);
    generateMessages(circleData);
    setLoading(false);
  };

  const generateMessages = (circleData) => {
    const msgs = circleData.map(circle => {
      const personName = circle.name.split(/[+,&]/).map(n => n.trim())[0] || circle.name;
      const shareLink = `${window.location.origin}/join/${circle.inviteCode}`;
      const message = `${personName} hope you are well. Here is your family link for Art Ping Pong. Just click to join and share with others for them to join you as well. Have fun and let me know what you think! ${shareLink}`;
      
      return {
        circleName: circle.name,
        personName,
        inviteCode: circle.inviteCode,
        shareLink,
        message
      };
    });
    setMessages(msgs);
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  const copyMessage = async (message, index) => {
    await copyToClipboard(message);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllMessages = async () => {
    const allMessages = messages.map(m => m.message).join('\n\n');
    await copyToClipboard(allMessages);
    setCopiedType('messages');
    setTimeout(() => setCopiedType(null), 2000);
  };

  const copyCSV = async () => {
    const csv = [
      'Person Name,Circle Name,Invite Code,Share Link,Message',
      ...messages.map(m => 
        `"${m.personName}","${m.circleName}","${m.inviteCode}","${m.shareLink}","${m.message}"`
      )
    ].join('\n');
    
    await copyToClipboard(csv);
    setCopiedType('csv');
    setTimeout(() => setCopiedType(null), 2000);
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <BackBtn onClick={() => navigate('/circles')}>
            <ArrowLeft size={20} />
          </BackBtn>
          <HeaderTitle>Message Generator</HeaderTitle>
          <Spacer />
        </Header>
        <LoadingText>Loading your circles...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/circles')}>
          <ArrowLeft size={20} />
        </BackBtn>
        <HeaderTitle>Message Generator</HeaderTitle>
        <Spacer />
      </Header>

      <Instructions>
        Generated messages for {messages.length} circle{messages.length !== 1 ? 's' : ''} you created.
      </Instructions>

      {messages.length > 0 && (
        <>
          <ActionsRow>
            <ActionButton onClick={copyCSV}>
              {copiedType === 'csv' ? <Check size={18} /> : <Copy size={18} />}
              {copiedType === 'csv' ? 'Copied CSV!' : 'Copy CSV'}
            </ActionButton>
            <ActionButton onClick={copyAllMessages}>
              {copiedType === 'messages' ? <Check size={18} /> : <Copy size={18} />}
              {copiedType === 'messages' ? 'Copied!' : 'Copy All Messages'}
            </ActionButton>
          </ActionsRow>

          <ResultCards>
            {messages.map((msg, index) => (
              <ResultCard key={index}>
                <CardHeader>
                  <CardName>{msg.personName}</CardName>
                  <CardCircle>{msg.circleName}</CardCircle>
                  <CardCode><Code>{msg.inviteCode}</Code></CardCode>
                </CardHeader>
                <CardLink>
                  <LinkText>{msg.shareLink}</LinkText>
                </CardLink>
                <CardMessage>
                  <MessageText>{msg.message}</MessageText>
                  <CopyBtn onClick={() => copyMessage(msg.message, index)}>
                    {copiedIndex === index ? <Check size={16} /> : <Copy size={16} />}
                  </CopyBtn>
                </CardMessage>
              </ResultCard>
            ))}
          </ResultCards>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 0 16px 32px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 16px;
`;

const BackBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  flex: 1;
`;

const Spacer = styled.div`
  width: 36px;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Instructions = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => theme.radius.medium};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Code = styled.code`
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 4px;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 15px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  margin: 24px 0 16px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ResultCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardName = styled.span`
  font-weight: 700;
  font-size: 16px;
`;

const CardCircle = styled.span`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CardCode = styled.span`
  margin-left: auto;
`;

const CardLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const LinkText = styled.span`
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
`;

const CardMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
`;

const MessageText = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.6;
  flex: 1;
`;

const CopyBtn = styled.button`
  padding: 6px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;
